import React from "react";
import PropTypes from "prop-types";
import { FaBed, FaBath } from "react-icons/fa";
import { BiArea } from "react-icons/bi";
import { MdLocationOn } from "react-icons/md";
import {
  PropertyPageCard,
  PropertyImageContainer,
  PropertyCategory,
  PropertyPostedDate,
  PropertyPageInfo,
  PropertyPageType,
  PropertyPagePrice,
  PropertyPriceDetail,
  PropertyPageName,
  PropertyPageAddress,
  PropertyPageDetails,
  PropertyPageDetail,
} from "./PropertyElements";
import { CardContainer, CardContent, CardWrapper } from "../Card/CardElements";

const PropertyCard = ({
  propertyId,
  imageUrl,
  category,
  postedDate,
  propertyType,
  price,
  name,
  propertyName,
  address,
  bedrooms,
  bathrooms,
  landSize,
  images,
  ...props
}) => {
  // Format the number of images for display
  const imageCount = images?.length || 0;

  // Determine if property is for sale or rent for badge styling
  const propertyTypeClass = propertyType?.toLowerCase().includes("sale")
    ? "sale"
    : propertyType?.toLowerCase().includes("rent")
      ? "rent"
      : "";

  return (
    <CardContainer flexBasis={props.flexBasis}>
      <PropertyPageCard to={`/property/${propertyId}`}>
        <PropertyImageContainer>
          {imageUrl ? (
            <img src={imageUrl} alt={name || propertyName} />
          ) : (
            <div
              className="no-image-placeholder"
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#f0f0f0",
                color: "#888",
                fontSize: 18,
              }}
            >
              No Image Available
            </div>
          )}

          <PropertyCategory data-type={propertyTypeClass}>
            {propertyType}
          </PropertyCategory>

          {imageCount > 0 && (
            <PropertyPostedDate>
              <svg
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z"></path>
                <path
                  fillRule="evenodd"
                  d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"
                  clipRule="evenodd"
                ></path>
              </svg>
              {imageCount} {imageCount === 1 ? "Photo" : "Photos"}
            </PropertyPostedDate>
          )}
        </PropertyImageContainer>

        <CardContent>
          <CardWrapper>
            <PropertyPageInfo>
              <PropertyPageName>{propertyName || name}</PropertyPageName>

              <PropertyPagePrice>
                N${" "}
                {parseFloat(price).toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                })}
                {propertyType?.toLowerCase().includes("rent") && (
                  <PropertyPriceDetail> / month</PropertyPriceDetail>
                )}
              </PropertyPagePrice>

              <PropertyPageAddress>
                <MdLocationOn />
                {address}
              </PropertyPageAddress>

              <PropertyPageDetails>
                <PropertyPageDetail>
                  <FaBed />
                  <span>{bedrooms}</span>
                  <span>Bedroom{bedrooms !== 1 ? "s" : ""}</span>
                </PropertyPageDetail>

                <PropertyPageDetail>
                  <FaBath />
                  <span>{bathrooms}</span>
                  <span>Bathroom{bathrooms !== 1 ? "s" : ""}</span>
                </PropertyPageDetail>

                <PropertyPageDetail>
                  <BiArea />
                  <span>{landSize}</span>
                  <span>m² Land</span>
                </PropertyPageDetail>
              </PropertyPageDetails>

              {category && (
                <PropertyPageType>
                  {getCategoryEmoji(category)} {category}
                </PropertyPageType>
              )}
            </PropertyPageInfo>
          </CardWrapper>
        </CardContent>
      </PropertyPageCard>
    </CardContainer>
  );
};

// Helper function to add emojis based on category
const getCategoryEmoji = (category) => {
  const categoryLower = category?.toLowerCase() || "";

  if (categoryLower.includes("house")) return "🏠";
  if (categoryLower.includes("apartment") || categoryLower.includes("flat"))
    return "🏢";
  if (categoryLower.includes("farm")) return "🌾";
  if (categoryLower.includes("commercial")) return "🏢";
  if (categoryLower.includes("land")) return "🏞️";

  return "🏘️";
};

PropertyCard.propTypes = {
  propertyId: PropTypes.string.isRequired,
  imageUrl: PropTypes.string,
  category: PropTypes.string.isRequired,
  postedDate: PropTypes.string,
  propertyType: PropTypes.string.isRequired,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string,
  propertyName: PropTypes.string,
  address: PropTypes.string,
  bedrooms: PropTypes.number.isRequired,
  bathrooms: PropTypes.number.isRequired,
  landSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      url: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  flexBasis: PropTypes.string,
};

PropertyCard.defaultProps = {
  imageUrl: "",
  address: "",
  images: [],
  name: "",
  propertyName: "",
  postedDate: "",
  flexBasis: "100%",
};

export default PropertyCard;
